import { Icon, Text, View } from '@/components'
import { theme } from '@/config'
import { AppIcon, createStyles } from '@codeleap/styles'
import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { chatMutedActionProps, chatPinnedActionProps, isChatMarkableAsRead } from '@packages/chat'
import { Channel } from 'stream-chat'

export type ChatPreviewAction =
  | 'mark-as-unread'
  | 'pin'
  | 'mute-unmute'
  // | 'delete-history'
  | 'delete-chat'

export type ChatPreviewActionItem = {
  action: ChatPreviewAction
  label: string
  icon: AppIcon
  onPress: (e?: React.MouseEvent) => void
  variant?: 'default' | 'danger'
}

export type ChatPreviewActionMenuProps = {
  channel: Channel
  onMarkAsUnread?: () => void
  onPin?: () => void
  onMuteUnmute?: (e?: React.MouseEvent) => void
  onDeleteChat?: (e?: React.MouseEvent) => void
  isAdmin?: boolean
  isGroup?: boolean
  isPinned?: boolean
}

const ChatPreviewActionMenuComponent: React.FC<ChatPreviewActionMenuProps> = (props) => {
  const { channel, onMarkAsUnread, onPin, onMuteUnmute, onDeleteChat, isAdmin = false, isGroup = false, isPinned = false } = props
  const [isOpen, setIsOpen] = useState(false)
  const [openUpwards, setOpenUpwards] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 })
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)

  const isMarkableAsRead = isChatMarkableAsRead(channel)
  const mutedAction = chatMutedActionProps(channel)
  const pinnedAction = chatPinnedActionProps(isPinned)

  const canDeleteHistory = !isGroup || isAdmin

  const allActions: ChatPreviewActionItem[] = [
    {
      action: 'mark-as-unread',
      label: isMarkableAsRead ? 'Mark as read' : 'Mark as unread',
      icon: 'message-notification-circle',
      onPress: () => {
        onMarkAsUnread?.()
        setIsOpen(false)
      },
    },
    {
      action: 'pin',
      label: pinnedAction.text,
      icon: pinnedAction.icon as ChatPreviewActionItem['icon'],
      onPress: () => {
        onPin?.()
        setIsOpen(false)
      },
    },
    {
      action: 'mute-unmute',
      label: mutedAction.text,
      icon: mutedAction.icon as ChatPreviewActionItem['icon'],
      onPress: (e?: React.MouseEvent) => {
        e?.stopPropagation()
        onMuteUnmute?.(e)
        setIsOpen(false)
      },
    },
    {
      action: 'delete-chat',
      label: 'Delete chat',
      icon: 'trash-03',
      onPress: (e?: React.MouseEvent) => {
        e?.stopPropagation()
        onDeleteChat?.(e)
        setIsOpen(false)
      },
      variant: 'danger',
    },
  ]

  // Filter out destructive chat actions if user doesn't have permission
  const actions = allActions.filter(action => {
    // if (action.action === 'delete-history' || action.action === 'delete-chat') {
    if (action.action === 'delete-chat') {
      return canDeleteHistory
    }
    return true
  })

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const calculateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return

    const buttonRect = buttonRef.current.getBoundingClientRect()
    const menuHeight = menuRef.current?.offsetHeight || 96
    const safeViewportHeight = (window.visualViewport?.height || window.innerHeight) - 60
    const gap = 2

    const spaceBelow = safeViewportHeight - buttonRect.bottom
    const shouldOpenUpwards = spaceBelow < menuHeight

    setOpenUpwards(shouldOpenUpwards)

    const rightPosition = window.innerWidth - buttonRect.right
    const topPosition = shouldOpenUpwards
      ? Math.max(gap, buttonRect.top - menuHeight - gap)
      : Math.min(buttonRect.bottom + gap, safeViewportHeight - menuHeight - gap)

    setMenuPosition({ top: topPosition, right: rightPosition })
  }, [])

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        calculateMenuPosition()
      })
    }
  }, [isOpen, calculateMenuPosition])

  const toggleMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    calculateMenuPosition()
    setIsOpen(prev => !prev)
  }, [calculateMenuPosition])

  return (
    <>
      <style>{`
        .chat-preview-action-button:hover {
          background-color: ${theme.colors.tagBgHover} !important;
        }
      `}</style>
      <div
        className='chat-preview-action-menu-wrapper'
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          zIndex: 10,
        }}
      >
        <View style={styles.container}>
          <View
            ref={buttonRef}
            onClick={toggleMenu}
            style={{
              ...styles.menuActionButton,
              backgroundColor: isOpen ? theme.colors.tagBgHover : 'transparent',
            }}
            className='chat-preview-action-button'
          >
            <Icon
              name={isOpen ? 'chevron-up' : 'chevron-down'}
              style={{
                fontSize: 12,
                color: theme.colors.textHeadingRegular,
                width: 12,
                height: 12,
              }}
            />
          </View>

        </View>
      </div>

      {isOpen && createPortal(
        <View
          ref={menuRef}
          style={{
            ...styles.menu,
            position: 'fixed',
            top: `${menuPosition.top}px`,
            right: `${menuPosition.right}px`,
            zIndex: 9999,
          }}
        >
          {actions.map((item, index) => (
            <React.Fragment key={item.action}>
              {index > 0 && (
                <View style={styles.separator} />
              )}
              <View
                debugName={`chat-preview-action:${item.action}`}
                style={{
                  ...styles.menuItem,
                  ...(item.variant === 'danger' ? styles.menuItemDanger : {}),
                }}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  item.onPress(e)
                }}
              >
                <Text
                  text={item.label}
                  style={[
                    'medium',
                    {
                      fontSize: 14,
                      color: item.variant === 'danger'
                        ? theme.colors.buttonNegativeQuaternaryLabelDefault
                        : theme.colors.textHeadingRegular,
                    },
                  ]}
                />
                <Icon
                  name={item.icon}
                  style={[
                    styles.icon,
                    {
                      color: item.variant === 'danger'
                        ? theme.colors.buttonNegativeQuaternaryIconDefault
                        : theme.colors.textHeadingRegular,
                    },
                  ]}
                />
              </View>
            </React.Fragment>
          ))}
        </View>,
        document.body,
      )}
    </>
  )
}

const styles = createStyles(theme => ({
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  menu: {
    backgroundColor: '#fff',
    borderRadius: theme.radius.regular,
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
    width: 200,
    height: 'auto',
    overflow: 'hidden',
    border: `1px solid ${'neutralBlackTransparent100'}`,
    display: 'flex',
    flexDirection: 'column',
  },
  icon: {
    ...theme.sized(2.5),
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.value(1),
    padding: `${theme.spacing.value(1.5)}px ${theme.spacing.value(2)}px`,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',

    '&:hover': {
      backgroundColor: theme.colors.rowBgHover,
    },
  },
  separator: {
    width: '100%',
    height: 1,
    backgroundColor: theme.baseColors.neutralBlackTransparent100,
  },
  menuItemDanger: {
    '&:hover': {
      backgroundColor: theme.colors.rowBgHover,
    },
  },
  menuActionButton: {
    cursor: 'pointer',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
  },
}))

export const ChatPreviewActionMenu = memo(
  ChatPreviewActionMenuComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.channel?.id === nextProps.channel?.id &&
      prevProps.onMarkAsUnread === nextProps.onMarkAsUnread &&
      prevProps.onPin === nextProps.onPin &&
      prevProps.onMuteUnmute === nextProps.onMuteUnmute &&
      prevProps.onDeleteChat === nextProps.onDeleteChat &&
      prevProps.isAdmin === nextProps.isAdmin &&
      prevProps.isGroup === nextProps.isGroup
    )
  },
)
